import { AlertTriangle, Lock } from 'lucide-react';
import type { ReactNode } from 'react';
import { useSession } from '@/components/auth/session';
import { EmptyState } from '@/components/common/bits';
import { Page, ViewHeader } from '@/components/layout/Page';
import { Button } from '@/components/ui/button';
import { Panel } from '@/components/ui/panel';
import { ApiError } from '@/lib/api';

/**
 * What the page reads, so the boundary drawn here is the one the server keeps.
 *
 * The page's own first query, passed as the hook that runs it: `AdminOnly` and
 * the page beneath it call the same hook with the same key, so react-query
 * serves both from one request rather than two. Anything with `isPending`,
 * `error` and `refetch` will do, which is every `useQuery` result.
 */
export interface AdminGate {
  isPending: boolean;
  error: unknown;
  refetch: () => void;
}

/** The hub's own sentence when it refuses, or null when it did not. */
function refusal(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.status !== 403) return null;
  return error.message;
}

/** One panel, so a refusal and a failure are the same shape with different words. */
function Notice({
  what,
  icon,
  title,
  hint,
  action,
}: {
  what: string;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  hint: string;
  action?: ReactNode;
}) {
  return (
    <>
      <ViewHeader title={what} />
      <Page>
        <Panel>
          <EmptyState icon={icon} title={title} hint={hint} action={action} />
        </Panel>
      </Page>
    </>
  );
}

/**
 * The admin sections, with the server as the authority.
 *
 * This used to be presentation and nothing else: it read `session.isAdmin` and
 * decided, while `/api/workers`, `/api/users`, `/api/invites` and `/api/events`
 * served anyone who was logged in at all. The boundary was a CSS rule a person
 * found by typing a URL, which PRODUCT.md rules out in as many words.
 *
 * Now the guard decides and this renders the answer. A 403 shows the hub's own
 * sentence rather than one written here, so a refusal cannot say something the
 * server does not mean.
 *
 * A failure that is not a refusal gets said out loud rather than passed through.
 * Rendering the page for a 500 would draw Fleet or People with no rows in it,
 * which reads as an empty fleet or a hub with nobody on it: the two facts a
 * person would most confidently act on, and both wrong. `session.isAdmin` is
 * still consulted for one thing only: while the first read is in flight it
 * decides whether to wait quietly or to draw the page. That is a guess about
 * latency, not about permission, and the server's answer replaces it either way.
 */
export function AdminOnly({
  what,
  useGate,
  children,
}: {
  what: string;
  useGate: () => AdminGate;
  children: ReactNode;
}) {
  const { session } = useSession();
  const gate = useGate();
  const refused = refusal(gate.error);

  if (refused) {
    return <Notice what={what} icon={Lock} title={`${what} is not yours to read`} hint={refused} />;
  }

  if (gate.error) {
    return (
      <Notice
        what={what}
        icon={AlertTriangle}
        title={`${what} could not be loaded`}
        hint={gate.error instanceof Error ? gate.error.message : 'the hub did not answer'}
        action={
          <Button size="sm" variant="outline" onClick={() => gate.refetch()}>
            Try again
          </Button>
        }
      />
    );
  }

  if (gate.isPending && !session.isAdmin) {
    return (
      <>
        <ViewHeader title={what} />
        <Page>
          <Panel>
            <EmptyState title={`Checking whether ${what.toLowerCase()} is yours to read…`} />
          </Panel>
        </Page>
      </>
    );
  }

  return <>{children}</>;
}
